import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import screenfull from 'screenfull'

import { ERROR_MESSAGES } from '~/constants/messages'
import useHandleError from '~/hooks/useHandleError'
import usePreview from '~/hooks/usePreview'
import counter from '~/services/counter'
import type { PreviewScreenProps, StartPreviewReqData } from '~/types/preview'
import { transformBranches } from '~/utils/dataTransformers'

import Button from '../common/Button'
import Select from '../common/Select'

const SelectLocationForm = ({ sound = false }: PreviewScreenProps) => {
  const handleError = useHandleError()
  const { startPreview } = usePreview()

  const [room, setRoom] = useState<string>()

  const {
    handleSubmit,
    register,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<StartPreviewReqData>()

  const selectedBranch = watch('branch')

  const branchQuery = useQuery({
    queryKey: ['branches'],
    queryFn: counter.getBranches,
    select: transformBranches,
    onError: handleError,
  })

  const counterQuery = useQuery({
    queryKey: ['counters', selectedBranch?.value],
    queryFn: () => counter.getCounters(selectedBranch?.value as number),
    enabled: !!selectedBranch?.value,
    onSuccess: (data) => {
      const counterCodes = data.data.map((counter) => `"${selectedBranch?.value}${counter.counter_code}"`)
      const roomString = `[${counterCodes.join(',')}]`
      setRoom(roomString)
    },
    onError: handleError,
  })

  const onSubmit: SubmitHandler<StartPreviewReqData> = (data) => {
    if (data.branch && room) {
      startPreview(data.branch, room, sound)
      screenfull
        .request()
        .then(() => toast.success('fullscreen enabled'))
        .catch(() => toast.error('fullscreen not allowed'))
    }
  }

  useEffect(() => {
    register('branch', { required: ERROR_MESSAGES.REQUIRED_COUNTER })
  }, [register])

  return (
    <div className="w-80 scale-110 rounded-lg bg-white p-6 shadow">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <div>
          <label className="mb-4 block text-center text-xl text-jba-blue">Pilih cabang JBA</label>
          <Select
            placeholder="Pilih cabang"
            icon="mdi:map-marker-radius"
            rounded="xl"
            options={branchQuery.data ?? []}
            isLoading={branchQuery.isLoading}
            isLocation
            selected={selectedBranch}
            setSelected={(value) => {
              setValue('branch', value)
              clearErrors('branch')
            }}
            disabled={branchQuery.isLoading}
            errorMessage={errors.branch?.message as string}
          />
        </div>
        <Button
          type="submit"
          variant="secondary"
          rounded="xl"
          block
          isLoading={!!(selectedBranch?.value && counterQuery.isLoading)}
          disabled={!room}
        >
          {selectedBranch?.value && counterQuery.isLoading ? 'Mengambil data counter..' : 'Teruskan'}
        </Button>
      </form>
    </div>
  )
}

export default SelectLocationForm
